import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  ArrowLeft, User, MapPin, Phone, Mail, Calendar, Clock,
  ChevronDown, ChevronUp, Camera, AlertTriangle, Check, X,
  Sparkles, Home, FileText, MessageSquare
} from 'lucide-react';
import EstimateCalculator from '../components/EstimateCalculator';
import { formatFullName, isCustomEstimate } from '../utils/helpers';
import { homeConditions, cleaningFrequencies } from '../utils/estimateData';
import './EstimateNew.css';
import './EstimateEdit.css';

function EstimateEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getEstimateById, getClientById, getPropertyById, updateEstimate } = useApp();

  const estimate = getEstimateById(id);
  const client = estimate ? getClientById(estimate.clientId) : null;
  const property = estimate ? getPropertyById(estimate.propertyId) : null;

  // Sidebar section toggles
  const [expandedSections, setExpandedSections] = useState({
    client: true,
    property: true,
    addons: true,
    notes: true,
    photos: false
  });

  // Selected photo for enlarged view
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Redirect if estimate not found
  useEffect(() => {
    if (!estimate) {
      navigate('/admin/estimates');
    }
  }, [estimate, navigate]);

  // Redirect custom estimates to the custom estimate editor
  useEffect(() => {
    if (estimate && isCustomEstimate(estimate)) {
      navigate(`/admin/estimates/custom/${id}`, { replace: true });
    }
  }, [estimate, id, navigate]);

  if (!estimate) {
    return null;
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Check if this is from website intake
  const isWebsiteIntake = estimate.source === 'website_intake';

  // Get condition and frequency labels
  const conditionConfig = homeConditions[estimate.calculatorInputs?.condition];
  const frequencyConfig = cleaningFrequencies[estimate.calculatorInputs?.frequency];

  // Handle save as draft
  const handleSaveDraft = async (data) => {
    const updatedEstimate = buildEstimateData(data);
    await updateEstimate(estimate.id, updatedEstimate);
    navigate(`/admin/estimates/${estimate.id}`);
  };

  // Handle save and preview
  const handleSaveAndPreview = async (data) => {
    const updatedEstimate = buildEstimateData(data);
    await updateEstimate(estimate.id, updatedEstimate);
    navigate(`/admin/estimates/${estimate.id}`);
  };

  // Build estimate data from calculator output
  const buildEstimateData = (data) => {
    const { inputs, selectedFrequencies, selectedAddons, estimates, requiresReview, priceOverrides, discounts, skipDeepClean, lineItemDescriptions } = data;
    
    console.log('Building estimate data:', {
      priceOverrides,
      estimates: Object.entries(estimates).map(([k, v]) => ({ freq: k, total: v.total, priceOverride: v.priceOverride }))
    });
    
    // Get the primary frequency
    const primaryFreq = selectedFrequencies.includes('weekly') ? 'weekly' 
      : selectedFrequencies.includes('biweekly') ? 'biweekly'
      : selectedFrequencies[0];
    
    const primaryEstimate = estimates[primaryFreq];
    
    console.log('Primary estimate:', { primaryFreq, total: primaryEstimate?.total, priceOverride: primaryEstimate?.priceOverride });

    // Build frequency options for client portal
    const frequencyLabels = {
      weekly: 'Weekly',
      biweekly: 'Bi-Weekly',
      monthly: 'Monthly',
      one_time: 'One-Time'
    };
    
    // Build frequency options with correct pricing
    const frequencyOptions = selectedFrequencies
      .filter(freq => estimates[freq] && !estimates[freq].requiresCustomQuote)
      .map(freq => {
        const est = estimates[freq];
        return {
          value: freq,
          label: frequencyLabels[freq] || freq,
          price: Math.round((est?.total || 0) * 100) / 100
        };
      });
    
    return {
      serviceType: 'residential',
      source: estimate.source || 'residential_calculator',
      calculatorInputs: {
        ...inputs,
        frequency: primaryFreq
      },
      selectedFrequencies,
      frequencyOptions, // For client portal
      allEstimates: Object.entries(estimates).reduce((acc, [freq, est]) => {
        if (!est.requiresCustomQuote) {
          acc[freq] = {
            lineItems: est.lineItems,
            total: est.total,
            deepCleanTotal: est.deepCleanEstimate?.subtotal || null
          };
        }
        return acc;
      }, {}),
      lineItems: (primaryEstimate?.lineItems || []).map(item => ({
        ...item,
        clientDescription: lineItemDescriptions?.[item.id] || item.clientDescription || ''
      })),
      deepCleanLineItems: (primaryEstimate?.deepCleanEstimate?.lineItems ||
        // For one-time services, addons are in lineItems not deepCleanEstimate
        (primaryEstimate?.lineItems || []).filter(item => item.type === 'addon')
      ).map(item => ({
        ...item,
        clientDescription: lineItemDescriptions?.[`dc_${item.id}`] || lineItemDescriptions?.[item.id] || item.clientDescription || ''
      })),
      lineItemDescriptions: lineItemDescriptions || {},
      recurringTotal: Math.round((primaryEstimate?.total || 0) * 100) / 100,
      deepCleanTotal: primaryEstimate?.deepCleanEstimate?.subtotal || null,
      skipDeepClean: skipDeepClean || false,
      requiresReview,
      reviewReasons: requiresReview ? ['Home condition or add-on requires review'] : [],
      selectedAddons,
      preferredDate: inputs.preferredDate,
      preferredTime: inputs.preferredTime,
      internalNotes: inputs.internalNotes,
      priceOverrides: priceOverrides || {},
      discounts: discounts || {},
      frequencyNotes: data.frequencyNotes || {}
    };
  };

  // Build initial values from existing estimate
  const initialValues = {
    sqft: estimate.calculatorInputs?.sqft || '',
    condition: estimate.calculatorInputs?.condition || '',
    bedrooms: estimate.calculatorInputs?.bedrooms || 3,
    fullBaths: estimate.calculatorInputs?.fullBaths || 2,
    halfBaths: estimate.calculatorInputs?.halfBaths || 0,
    people: estimate.calculatorInputs?.people || 3,
    sheddingPets: estimate.calculatorInputs?.sheddingPets || 0,
    glassShowerDoors: estimate.calculatorInputs?.glassShowerDoors || 0,
    excludedRooms: estimate.calculatorInputs?.excludedRooms || [],
    preferredDate: estimate.preferredDate || '',
    preferredTime: estimate.preferredTime || '',
    internalNotes: estimate.internalNotes || '',
    lineItemDescriptions: estimate.lineItemDescriptions || {},
    selectedFrequencies: estimate.selectedFrequencies || [estimate.calculatorInputs?.frequency].filter(Boolean),
    addons: estimate.selectedAddons || [],
    priceOverrides: estimate.priceOverrides || {},
    discounts: estimate.discounts || {},
    frequencyNotes: estimate.frequencyNotes || {}
  };

  return (
    <div className="estimate-edit-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(`/admin/estimates/${estimate.id}`)}>
          <ArrowLeft size={20} />
        </button>
        <div className="header-content">
          <h1>Edit Estimate</h1>
          <p className="header-subtitle">
            {isWebsiteIntake ? 'Review client submission and complete estimate' : 'Modify estimate details'}
          </p>
        </div>
      </div>

      <div className="edit-layout">
        {/* Reference Sidebar - Client Submitted Info */}
        {isWebsiteIntake && (
          <div className="reference-sidebar">
            <div className="sidebar-header">
              <FileText size={18} />
              <span>Client Submission</span>
            </div>

            {/* Client Info Section */}
            <div className="sidebar-section">
              <div 
                className="section-header"
                onClick={() => toggleSection('client')}
              >
                <User size={16} />
                <span>Client Info</span>
                {expandedSections.client ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
              {expandedSections.client && (
                <div className="section-content">
                  <div className="info-row">
                    <span className="info-label">Name</span>
                    <span className="info-value">{formatFullName(client)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Email</span>
                    <a href={`mailto:${client?.email}`} className="info-value link">
                      {client?.email}
                    </a>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Phone</span>
                    <a href={`tel:${client?.phone}`} className="info-value link">
                      {client?.phone}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Property Info Section */}
            <div className="sidebar-section">
              <div 
                className="section-header"
                onClick={() => toggleSection('property')}
              >
                <Home size={16} />
                <span>Property Details</span>
                {expandedSections.property ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
              {expandedSections.property && (
                <div className="section-content">
                  <div className="info-row">
                    <span className="info-label">Address</span>
                    <span className="info-value">
                      {property?.street || estimate.propertyAddress?.split(',')[0]}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">City</span>
                    <span className="info-value">{property?.city}, {property?.state} {property?.zip}</span>
                  </div>
                  <div className="info-row highlight">
                    <span className="info-label">Sqft</span>
                    <span className="info-value">
                      {estimate.calculatorInputs?.sqft 
                        ? estimate.calculatorInputs.sqft.toLocaleString() 
                        : <span className="needs-input">Needs input ←</span>
                      }
                    </span>
                  </div>
                  <div className="property-stats">
                    <div className="stat-item">
                      <span className="stat-value">{estimate.calculatorInputs?.bedrooms || '—'}</span>
                      <span className="stat-label">Beds</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">
                        {(estimate.calculatorInputs?.fullBaths || 0) + (estimate.calculatorInputs?.halfBaths || 0) * 0.5}
                      </span>
                      <span className="stat-label">Baths</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{estimate.calculatorInputs?.people || '—'}</span>
                      <span className="stat-label">People</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{estimate.calculatorInputs?.sheddingPets || 0}</span>
                      <span className="stat-label">Pets</span>
                    </div>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Condition</span>
                    <span className="info-value condition">
                      {conditionConfig?.icon} {conditionConfig?.label || '—'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Frequency</span>
                    <span className="info-value">{frequencyConfig?.label || '—'}</span>
                  </div>
                  {estimate.preferredDay && (
                    <div className="info-row">
                      <span className="info-label">Preferred Day</span>
                      <span className="info-value">{estimate.preferredDay}</span>
                    </div>
                  )}
                  {estimate.preferredTime && (
                    <div className="info-row">
                      <span className="info-label">Preferred Time</span>
                      <span className="info-value">{estimate.preferredTime}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Requested Add-ons Section */}
            {estimate.selectedAddons?.length > 0 && (
              <div className="sidebar-section">
                <div 
                  className="section-header"
                  onClick={() => toggleSection('addons')}
                >
                  <Sparkles size={16} />
                  <span>Requested Add-ons ({estimate.selectedAddons.length})</span>
                  {expandedSections.addons ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
                {expandedSections.addons && (
                  <div className="section-content">
                    {estimate.selectedAddons.map((addon, idx) => (
                      <div key={idx} className="addon-item">
                        <div className="addon-main">
                          <Check size={14} className="addon-check" />
                          <span className="addon-name">{addon.label || addon.id}</span>
                        </div>
                        {addon.quantity && (
                          <span className="addon-qty">×{addon.quantity}</span>
                        )}
                        {addon.hours && (
                          <span className="addon-qty">{addon.hours}hr</span>
                        )}
                        {addon.isBlueCeramic && (
                          <div className="addon-warning">
                            <AlertTriangle size={12} />
                            Blue ceramic oven
                          </div>
                        )}
                        {addon.requiresReview && (
                          <div className="addon-note">Custom quote needed</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notes & Concerns Section */}
            {(estimate.specialRequests || estimate.productConcerns || estimate.approvedProducts === false) && (
              <div className="sidebar-section">
                <div 
                  className="section-header"
                  onClick={() => toggleSection('notes')}
                >
                  <MessageSquare size={16} />
                  <span>Notes & Concerns</span>
                  {expandedSections.notes ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
                {expandedSections.notes && (
                  <div className="section-content">
                    {estimate.approvedProducts === false && (
                      <div className="concern-box">
                        <div className="concern-header">
                          <AlertTriangle size={14} />
                          Product Concerns
                        </div>
                        <p>{estimate.productConcerns || 'Client has concerns about cleaning products'}</p>
                      </div>
                    )}
                    {estimate.approvedProducts === true && (
                      <div className="approved-box">
                        <Check size={14} />
                        <span>Products approved</span>
                      </div>
                    )}
                    {estimate.specialRequests && (
                      <div className="notes-box">
                        <div className="notes-header">Special Requests</div>
                        <p>{estimate.specialRequests}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Photos Section */}
            {estimate.intakePhotos?.length > 0 && (
              <div className="sidebar-section">
                <div 
                  className="section-header"
                  onClick={() => toggleSection('photos')}
                >
                  <Camera size={16} />
                  <span>Photos ({estimate.intakePhotos.length})</span>
                  {expandedSections.photos ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
                {expandedSections.photos && (
                  <div className="section-content">
                    <div className="photos-grid">
                      {estimate.intakePhotos.map((photo, idx) => (
                        <div 
                          key={idx} 
                          className="photo-thumb"
                          onClick={() => setSelectedPhoto(photo)}
                        >
                          <img src={photo.preview} alt={photo.label || `Photo ${idx + 1}`} />
                          {photo.label && <span className="photo-label">{photo.label}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Main Calculator Area */}
        <div className={`calculator-area ${isWebsiteIntake ? 'with-sidebar' : ''}`}>
          {/* Client & Property Summary for non-intake estimates */}
          {!isWebsiteIntake && (
            <div className="selected-summary compact">
              <div className="summary-section">
                <User size={16} />
                <span>{formatFullName(client)}</span>
              </div>
              <div className="summary-section">
                <MapPin size={16} />
                {(() => {
                  const streetPart = property?.street || property?.addressLine1 || estimate.propertyAddress || '';
                  const cityPart = property?.city || '';
                  const statePart = property?.state || '';
                  const zipPart = property?.zip || '';
                  const fullAddress = [streetPart, cityPart, statePart, zipPart].filter(Boolean).join(', ');
                  const displayAddress = `${streetPart || 'No address'}, ${cityPart}`;
                  return fullAddress ? (
                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent(fullAddress)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Search Google for this address"
                    >
                      {displayAddress}
                    </a>
                  ) : (
                    <span>{displayAddress}</span>
                  );
                })()}
              </div>
            </div>
          )}

          <EstimateCalculator
            propertyData={property}
            initialValues={initialValues}
            onSaveDraft={handleSaveDraft}
            onSaveAndPreview={handleSaveAndPreview}
          />
        </div>
      </div>

      {/* Photo Modal */}
      {selectedPhoto && (
        <div className="photo-modal" onClick={() => setSelectedPhoto(null)}>
          <div className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setSelectedPhoto(null)}>
              <X size={24} />
            </button>
            <img src={selectedPhoto.preview} alt={selectedPhoto.label || 'Photo'} />
            {selectedPhoto.label && (
              <div className="photo-modal-label">{selectedPhoto.label}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EstimateEdit;
